import { useState } from 'react';

import { QueueFilterValues, QueuePage } from '../api/types';
import NeedsApprovalRow from '../components/NeedsApprovalRow';
import NeedsApprovalReviewModal from '../components/NeedsApprovalReviewModal';
import useTimesheetReview from '../hooks/useTimesheetReview';
import QueueError from '../components/QueueError';
import QueuePagination from '../components/QueuePagination';
import QueueFilterHeader from '../components/QueueFilterHeader';
import ShiftDetailPanel from '../components/ShiftDetailPanel';
import useDashboardFilters from '../hooks/useDashboardFilters';
import useNeedsApprovalQueue from '../hooks/useNeedsApprovalQueue';
import useQueuePageClamp from '../hooks/useQueuePageClamp';

import LoadingState from '@/components/LoadingState';

// Needs-approval queue: shifts with a pending decision, one row each, with a
// fraction column whose sub-state (Hired / For Hired / For approval) is
// colour-distinguished.
//
// The chevron opens the shared Shift Detail panel over the queue rather than
// navigating to the legacy job page (GRAVY-10296). Selection lives here, not in
// the row, so the queue list, its scroll position and the loaded data all stay
// mounted underneath — closing the panel returns to exactly the same view. This
// is the same arrangement CalendarTab uses, and both drive the same panel from
// the same shift-scoped contract.

type Props = {
  agencyId: number;
  /** Owned by the page, so it survives tab switches (GRAVY-10303). */
  filters: QueueFilterValues;
  onFiltersChange: (next: QueueFilterValues) => void;
  /** Also owned by the page, for the same reason (GRAVY-10304). */
  page: QueuePage;
  onPageChange: (next: number) => void;
};

const NeedsApprovalTab = ({
  agencyId,
  filters,
  onFiltersChange,
  page,
  onPageChange,
}: Props) => {
  const { data, isLoading, isError, refetch } = useNeedsApprovalQueue(
    agencyId,
    filters,
    page,
  );
  const { data: filterOptions } = useDashboardFilters(agencyId);
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);
  const review = useTimesheetReview(agencyId);
  // A live refresh can shrink the queue under an admin working a later page,
  // stranding them on a page that no longer exists (GRAVY-10323).
  useQueuePageClamp(data?.pagination, page.page, onPageChange);

  const shifts = data?.shifts ?? [];

  return (
    <div>
      <QueueFilterHeader
        heading="Needs approval"
        queue="needs_approval"
        options={filterOptions}
        value={filters}
        onChange={onFiltersChange}
      />

      {isLoading && !data ? (
        <LoadingState />
      ) : isError ? (
        <QueueError
          message="Unable to load the needs-approval queue."
          onRetry={refetch}
        />
      ) : shifts.length === 0 ? (
        <div className="dashv2-queue-empty">
          {data && data.total > 0
            ? 'No results on this page.'
            : 'Nothing awaiting approval. 🎉'}
        </div>
      ) : (
        <div className="dashv2-queue-list">
          {shifts.map((shift) => (
            <NeedsApprovalRow
              key={shift.shift_id}
              shift={shift}
              onSelect={setSelectedShiftId}
              onReview={(timesheetId) => review.open(timesheetId)}
            />
          ))}
        </div>
      )}

      {data?.pagination && !isError && (
        <QueuePagination
          pagination={data.pagination}
          onPageChange={onPageChange}
          queue="Needs approval"
        />
      )}

      {/* GRAVY-10366 — the per-worker Review modal. Mounted once and reused per
          row; the hook re-seeds its local state when a different timesheet
          arrives. */}
      <NeedsApprovalReviewModal
        isOpen={review.isOpen}
        review={review.data}
        loading={review.loading}
        error={review.error}
        onClose={review.close}
        onApprove={review.approve}
        onReject={review.reject}
      />

      {selectedShiftId != null && (
        <ShiftDetailPanel
          agencyId={agencyId}
          shiftId={selectedShiftId}
          onClose={() => setSelectedShiftId(null)}
        />
      )}
    </div>
  );
};

export default NeedsApprovalTab;
